import t from "@/lang/lang";
import { useEffect, useState } from "react";
import {
  GfsPostalAddressCard,
  GfsRepresentativeCard,
} from "@/services/backend/gfs/offer/components/gfs-contact-fields";
import { useGfsOfferUseCustomerPostalAddressMutation } from "@/services/backend/gfs/offer/api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { useGfsOfferEditorContext } from "@/services/backend/gfs/offer/components/offer-editor-context";
import { InputDebounced } from "@/shared/components/inputs/input-debounced";
import { Button } from "@/shared/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import type { GfsRepresentativePhoneType } from "@/services/backend/gfs/offer/types";

export function GfsOfferContactPage() {
  const { putRequest, updateContent, saveState, offer } =
    useGfsOfferEditorContext();
  const representativePhoneType: GfsRepresentativePhoneType =
    putRequest.content.representativePhoneType === "mobile"
      ? "mobile"
      : "phone";
  const selectedRepresentativePhone =
    representativePhoneType === "mobile"
      ? putRequest.content.representative.mobilephone
      : putRequest.content.representative.phone;
  const [applyCustomerAddress, customerAddressState] =
    useGfsOfferUseCustomerPostalAddressMutation();
  const responsibleCount =
    putRequest.content.caseData.customerResponsibles.length;
  const [responsibleKeys, setResponsibleKeys] = useState<string[]>(() =>
    putRequest.content.caseData.customerResponsibles.map(() =>
      crypto.randomUUID(),
    ),
  );

  useEffect(() => {
    setResponsibleKeys((current) => {
      const retained = current.slice(0, responsibleCount);
      return [
        ...retained,
        ...Array.from({ length: responsibleCount - retained.length }, () =>
          crypto.randomUUID(),
        ),
      ];
    });
  }, [responsibleCount]);

  return (
    <div className="space-y-4 pb-16">
      {saveState.error && <RTKQueryErrorAlert error={saveState.error} />}
      {customerAddressState.error && (
        <RTKQueryErrorAlert error={customerAddressState.error} />
      )}
      <GfsPostalAddressCard
        address={putRequest.content.postalAddress}
        onAddressChange={(postalAddress) => updateContent({ postalAddress })}
        contactRole={putRequest.content.postalAddressContactRole}
        onContactRoleChange={(postalAddressContactRole) =>
          updateContent({ postalAddressContactRole })
        }
        disabled={offer.immutable}
        title={t("Postanschrift")}
        description={t(
          "Adresse des Kunden, wie sie auf dem Angebot erscheinen soll.",
        )}
      />
      <div className="flex justify-end">
        <Button
          variant="outline"
          disabled={offer.immutable || customerAddressState.isLoading}
          onClick={() => applyCustomerAddress({ offerId: offer.id })}
        >
          {t("Aus Kundendaten übernehmen")}
        </Button>
      </div>
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div className="space-y-1.5">
            <CardTitle>{t("Verantwortliche beim Kunden")}</CardTitle>
            <CardDescription>
              {t(
                "Diese Personen erscheinen gemeinsam mit dem Betreuer in der Zeile Verantwortliche.",
              )}
            </CardDescription>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={offer.immutable}
            onClick={() => {
              updateContent({
                caseData: {
                  ...putRequest.content.caseData,
                  customerResponsibles: [
                    ...putRequest.content.caseData.customerResponsibles,
                    { name: "", phone: "", role: t("Kunde") },
                  ],
                },
              });
              setResponsibleKeys((current) => [
                ...current,
                crypto.randomUUID(),
              ]);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            {t("Person")}
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {putRequest.content.caseData.customerResponsibles.length === 0 && (
            <p className="text-muted-foreground rounded-md border border-dashed p-4 text-sm">
              {t("Noch keine verantwortliche Person hinterlegt.")}
            </p>
          )}
          {putRequest.content.caseData.customerResponsibles.map(
            (person, index) => (
              <div
                className="grid gap-3 rounded-md border p-3 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,0.7fr)_auto] md:items-end"
                key={responsibleKeys[index] ?? `responsible-${index}`}
              >
                <ContactInput
                  label={t("Name")}
                  value={person.name}
                  disabled={offer.immutable}
                  onChange={(name) =>
                    updateResponsible(
                      putRequest.content.caseData,
                      index,
                      {
                        name,
                      },
                      updateContent,
                    )
                  }
                />
                <ContactInput
                  label={t("Telefon")}
                  value={person.phone}
                  disabled={offer.immutable}
                  onChange={(phone) =>
                    updateResponsible(
                      putRequest.content.caseData,
                      index,
                      {
                        phone,
                      },
                      updateContent,
                    )
                  }
                />
                <ContactInput
                  label={t("Rolle")}
                  value={person.role}
                  disabled={offer.immutable}
                  onChange={(role) =>
                    updateResponsible(
                      putRequest.content.caseData,
                      index,
                      {
                        role,
                      },
                      updateContent,
                    )
                  }
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={offer.immutable}
                  aria-label={t("Person entfernen")}
                  onClick={() =>
                    (() => {
                      setResponsibleKeys((current) =>
                        current.filter(
                          (_, candidateIndex) => candidateIndex !== index,
                        ),
                      );
                      updateContent({
                        caseData: {
                          ...putRequest.content.caseData,
                          customerResponsibles:
                            putRequest.content.caseData.customerResponsibles.filter(
                              (_, candidateIndex) => candidateIndex !== index,
                            ),
                        },
                      });
                    })()
                  }
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ),
          )}
        </CardContent>
      </Card>
      <GfsRepresentativeCard
        representative={putRequest.content.representative}
        onRepresentativeChange={(representative) =>
          updateContent({ representative })
        }
        disabled={offer.immutable}
      />
      <Card>
        <CardHeader>
          <CardTitle>{t("Funktion des Betreuers")}</CardTitle>
          <CardDescription>
            {t("Funktion oder Rolle unter dem Namen im Anschreiben.")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <label className="text-sm font-medium">{t("Funktion")}</label>
          <InputDebounced
            value={putRequest.content.representativeRole}
            disabled={offer.immutable}
            onChange={(event) =>
              updateContent({ representativeRole: event.target.value })
            }
          />
          <div className="space-y-2 pt-2">
            <label className="text-sm font-medium">
              {t("Nummer bei Verantwortliche")}
            </label>
            <div className="flex min-h-9 items-center gap-3">
              <Select
                value={representativePhoneType}
                onValueChange={(value) => {
                  const nextPhoneType: GfsRepresentativePhoneType =
                    value === "mobile" ? "mobile" : "phone";
                  updateContent({ representativePhoneType: nextPhoneType });
                }}
                disabled={offer.immutable}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="phone">{t("Telefon")}</SelectItem>
                  <SelectItem value="mobile">{t("Mobil")}</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-muted-foreground text-sm">
                {selectedRepresentativePhone || t("Keine Nummer hinterlegt")}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ContactInput({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <label className="space-y-2 text-sm font-medium">
      <span>{label}</span>
      <InputDebounced
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function updateResponsible(
  caseData: import("@/services/backend/gfs/offer/types").GfsOfferCaseData,
  index: number,
  patch: Partial<
    import("@/services/backend/gfs/offer/types").GfsOfferResponsiblePerson
  >,
  updateContent: ReturnType<typeof useGfsOfferEditorContext>["updateContent"],
) {
  updateContent({
    caseData: {
      ...caseData,
      customerResponsibles: caseData.customerResponsibles.map(
        (person, candidateIndex) =>
          candidateIndex === index ? { ...person, ...patch } : person,
      ),
    },
  });
}
